/* Samonde part search
   Query normalization + predictive search + zero-result logging.
   No dependencies. Attach by adding data-part-search to a wrapper element. */

(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  /* Logging waits much longer than the dropdown does. A miss is only worth
     recording once the buyer has stopped typing — otherwise one search for an
     unstocked part files six events ("QQ", "QQ7", "QQ7X"...) and the GA4
     report ranks prefixes instead of parts. */
  var LOG_SETTLE_MS = 1500;
  var MIN_CHARS = 2;
  var LIMIT = 8;

  /* Ops tags that must never be shown to a customer, taken from the tag
     vocabulary actually in use on the store. Without the full list, tags like
     tag-seo-optimized-2025, tag-40a and ebay-cat-181708 pass for part numbers:
     they carry digits, letters and a separator, which is all the shape test
     asks for. */
  var INTERNAL_TAG_PREFIXES = [
    'brand-', 'part-', 'tag-', 'ebay-', 'sync-', 'condition-',
    'weight-', 'availability-', 'needs-', 'publish-', 'vendly-'
  ];

  /* MPNs get typed three ways:
       6SN1114-0NB00-0AA1
       6SN1114 0NB00 0AA1
       6SN11140NB000AA1
     Strip separators so all three collapse to one comparable form.

     Note: this only rescues a query that HAS separators. A buyer who types the
     already-stripped form gets no fallback, because there is nothing left to
     strip. That case is fixed in the data — the stripped MPN has to exist as a
     product tag — not here. */
  function normalize(value) {
    return value.replace(/[\s.\-_/\\]+/g, '');
  }

  function tidy(value) {
    return value.trim().replace(/\s+/g, ' ');
  }

  function suggestUrl(query, root) {
    return (
      (root || '') +
      '/search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=' +
      LIMIT +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,vendor,product_type,variants.sku,variants.barcode,tag'
    );
  }

  function fetchProducts(query, root) {
    return fetch(suggestUrl(query, root), { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return (data && data.resources && data.resources.results && data.resources.results.products) || [];
      });
  }

  /* Try what the user typed. Only if that misses, try the normalized form.
     Two calls on a miss, one call on a hit. */
  function search(raw, root) {
    var typed = tidy(raw);
    var norm = normalize(typed);
    return fetchProducts(typed, root).then(function (products) {
      if (products.length || norm === typed) return products;
      return fetchProducts(norm, root);
    });
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function isInternalTag(tag) {
    var lower = String(tag).toLowerCase();
    for (var i = 0; i < INTERNAL_TAG_PREFIXES.length; i++) {
      if (lower.indexOf(INTERNAL_TAG_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  /* suggest.json returns variants: [] for every product on this store, so the
     variant SKU is never available here. The part number comes off the tags
     instead. Prefer the printed form (with separators) over the stripped one. */
  function partNumber(product, query) {
    var sku = product.variants && product.variants[0] && product.variants[0].sku;
    if (sku) return sku;

    var tags = product.tags || [];
    var wanted = query ? normalize(query).toUpperCase() : '';
    var candidates = [];
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i]).trim();
      if (isInternalTag(tag)) continue;
      if (tag.length < 5) continue;
      if (!/\d/.test(tag) || !/[A-Za-z]/.test(tag)) continue;
      if (!/^[A-Za-z0-9][A-Za-z0-9\-_/. ]*$/.test(tag)) continue;
      candidates.push(tag);
    }
    if (!candidates.length) return '';

    /* Show the part number the buyer actually matched on, in its printed form.
       A product carries the same MPN three ways once tagged, so pick the one
       that answers the query rather than whichever sorts first. */
    if (wanted) {
      for (var j = 0; j < candidates.length; j++) {
        var norm = normalize(candidates[j]).toUpperCase();
        if ((norm.indexOf(wanted) > -1 || wanted.indexOf(norm) > -1) && /[-_/. ]/.test(candidates[j])) return candidates[j];
      }
    }
    for (var k = 0; k < candidates.length; k++) {
      if (/[-_/. ]/.test(candidates[k])) return candidates[k];
    }
    return candidates[0];
  }

  /* Shopify's `t` filter HTML-escapes any translation whose key does not end in
     _html, so an apostrophe reaches us as "we&#39;ll". These strings are then
     escaped again on the way into innerHTML, which would render the entity as
     visible text. Decode once here so the escaping happens exactly once, and so
     a future translator adding an apostrophe cannot reintroduce this. */
  var ENTITIES = { '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&amp;': '&' };

  function decodeEntities(str) {
    return String(str).replace(/&(?:lt|gt|quot|#39|amp);/g, function (m) {
      return ENTITIES[m];
    });
  }

  function stockLabel(product, strings) {
    return product.available ? strings.inStock : strings.onRequest;
  }

  function renderResults(products, strings, query) {
    return products
      .map(function (p, i) {
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : null;
        var mpn = partNumber(p, query);
        return (
          '<li class="ps-result" role="option" id="ps-opt-' + i + '" aria-selected="false">' +
          '<a class="ps-result__link" href="' + escapeHtml(p.url) + '" tabindex="-1">' +
          (img
            ? '<img class="ps-result__img" src="' + escapeHtml(img) + '&width=80" alt="" width="40" height="40" loading="lazy">'
            : '<span class="ps-result__img ps-result__img--empty" aria-hidden="true"></span>') +
          '<span class="ps-result__body">' +
          '<span class="ps-result__title">' + escapeHtml(p.title) + '</span>' +
          '<span class="ps-result__meta">' +
          (p.vendor ? escapeHtml(p.vendor) : '') +
          (mpn ? '<span class="ps-result__sku">' + escapeHtml(mpn) + '</span>' : '') +
          '</span></span>' +
          '<span class="ps-result__stock' + (p.available ? '' : ' is-unavailable') + '">' +
          escapeHtml(stockLabel(p, strings)) +
          '</span>' +
          '</a></li>'
        );
      })
      .join('');
  }

  function renderEmpty(query, strings, searchPath) {
    return (
      '<li class="ps-empty" role="option" aria-selected="false">' +
      '<span class="ps-empty__title">' + strings.emptyTitle.replace('{{ q }}', escapeHtml(query)) + '</span>' +
      '<span class="ps-empty__body">' + escapeHtml(strings.emptyBody) + '</span>' +
      '<a class="ps-empty__cta" href="' + searchPath + '?q=' + encodeURIComponent(query) + '">' +
      escapeHtml(strings.emptyCta) +
      '</a></li>'
    );
  }

  /* Fires a GA4 / dataLayer event on a zero-result query. Tagged page:'dropdown'
     so it can be told apart from the one the search results page fires — the
     same buyer will often produce both. */
  function logMiss(query) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'search_no_results', { search_term: query, page: 'dropdown' });
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'search_no_results', search_term: query, page: 'dropdown' });
    } catch (e) {
      /* logging must never break search */
    }
  }

  function init(wrapper) {
    var input = wrapper.querySelector('[data-part-search-input]');
    var panel = wrapper.querySelector('[data-part-search-results]');
    var form = wrapper.querySelector('form');
    if (!input || !panel || !form) return;

    var root = wrapper.getAttribute('data-root') || '';
    if (root === '/') root = '';
    var searchPath = root + '/search';
    var strings = {};
    try {
      strings = JSON.parse(wrapper.getAttribute('data-strings') || '{}');
    } catch (e) {
      strings = {};
    }
    strings.inStock = decodeEntities(strings.inStock || 'In stock');
    strings.onRequest = decodeEntities(strings.onRequest || 'On request');
    strings.emptyTitle = decodeEntities(strings.emptyTitle || 'No listing for <b>{{ q }}</b>');
    strings.emptyBody = decodeEntities(strings.emptyBody || 'We source parts weekly. Tell us what you need and we will quote you.');
    strings.emptyCta = decodeEntities(strings.emptyCta || 'Request this part');

    var timer = null;
    var logTimer = null;
    var lastLogged = '';
    var active = -1;
    var lastQuery = '';
    var seq = 0;

    function close() {
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }

    function options() {
      return panel.querySelectorAll('[role="option"]');
    }

    function highlight(index) {
      var opts = options();
      if (!opts.length) return;
      if (index < 0) index = opts.length - 1;
      if (index >= opts.length) index = 0;
      Array.prototype.forEach.call(opts, function (el) {
        el.setAttribute('aria-selected', 'false');
        el.classList.remove('is-active');
      });
      opts[index].setAttribute('aria-selected', 'true');
      opts[index].classList.add('is-active');
      if (opts[index].id) input.setAttribute('aria-activedescendant', opts[index].id);
      active = index;
    }

    function scheduleMiss(query) {
      clearTimeout(logTimer);
      logTimer = setTimeout(function () {
        if (query !== lastLogged) {
          lastLogged = query;
          logMiss(query);
        }
      }, LOG_SETTLE_MS);
    }

    function run() {
      var query = tidy(input.value);
      if (query.length < MIN_CHARS) {
        close();
        return;
      }
      if (query === lastQuery && !panel.hidden) return;
      lastQuery = query;

      var mine = ++seq;
      wrapper.classList.add('is-loading');

      search(query, root)
        .then(function (products) {
          if (mine !== seq) return;
          if (products.length) {
            clearTimeout(logTimer);
            panel.innerHTML = renderResults(products, strings, query);
          } else {
            panel.innerHTML = renderEmpty(query, strings, searchPath);
            scheduleMiss(query);
          }
          panel.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          active = -1;
        })
        .catch(function () {
          if (mine !== seq) return;
          close();
        })
        .finally(function () {
          if (mine === seq) wrapper.classList.remove('is-loading');
        });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      clearTimeout(logTimer);
      timer = setTimeout(run, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      if (tidy(input.value).length >= MIN_CHARS && panel.innerHTML) {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight(active + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(active - 1);
      } else if (e.key === 'Enter' && active > -1) {
        var link = options()[active].querySelector('a');
        if (link) {
          e.preventDefault();
          window.location.href = link.href;
        }
      } else if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });

    /* On plain submit, send the normalized form too so the results page
       can fall back to it when the literal string misses. */
    form.addEventListener('submit', function () {
      clearTimeout(logTimer);
      var typed = tidy(input.value);
      var norm = normalize(typed);
      if (norm !== typed) {
        var hidden = form.querySelector('input[name="ps_norm"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'ps_norm';
          form.appendChild(hidden);
        }
        hidden.value = norm;
      }
      input.value = typed;
    });

    document.addEventListener('click', function (e) {
      if (!wrapper.contains(e.target)) close();
    });
  }

  function boot() {
    document.querySelectorAll('[data-part-search]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.SamondeSearch = { normalize: normalize, search: search, partNumber: partNumber };
})();
